import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { FileText, CreditCard, User, LogOut, Wifi, AlertTriangle, CheckCircle, Clock, X, Printer, Download, QrCode, Barcode, Smartphone } from 'lucide-react';
import axios from 'axios';
import html2pdf from 'html2pdf.js';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export default function PortalDashboard() {
  const [cliente, setCliente] = useState(null);
  const [facturas, setFacturas] = useState([]);
  const [selectedFactura, setSelectedFactura] = useState(null);
  const [pagosFactura, setPagosFactura] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mpLoading, setMpLoading] = useState(false);
  const [siroLoading, setSiroLoading] = useState(false);
  const [siroHabilitado, setSiroHabilitado] = useState(false);
  const [showSiroModal, setShowSiroModal] = useState(false);
  const [siroData, setSiroData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('cliente_token');
    const clienteData = localStorage.getItem('cliente_data');

    if (!token || !clienteData) {
      navigate('/portal/login');
      return;
    }

    setCliente(JSON.parse(clienteData));
    cargarFacturas(token);
    verificarSiro(token);
  }, [navigate]);

  const cargarFacturas = async (token) => {
    try {
      const clienteData = JSON.parse(localStorage.getItem('cliente_data'));
      const response = await axios.get(`${API_URL}/facturas?clienteId=${clienteData.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFacturas(response.data);
    } catch (error) {
      toast.error('Error al cargar facturas');
    } finally {
      setLoading(false);
    }
  };

  const verificarSiro = async (token) => {
    try {
      const response = await axios.get(`${API_URL}/configuraciones?categoria=pagos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const siroConfig = response.data.find(c => c.clave === 'SIRO_HABILITADO');
      setSiroHabilitado(siroConfig?.valor === 'true');
    } catch (error) {
      setSiroHabilitado(false);
    }
  };

  const verDetalleFactura = async (factura) => {
    setSelectedFactura(factura);
    const token = localStorage.getItem('cliente_token');
    try {
      const response = await axios.get(`${API_URL}/pagos?facturaId=${factura.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPagosFactura(response.data);
    } catch (error) {
      setPagosFactura([]);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('cliente_token');
    localStorage.removeItem('cliente_data');
    navigate('/portal/login');
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    const element = document.getElementById('comprobante-factura');
    const opt = {
      margin: [10, 10, 10, 10],
      filename: `factura-${selectedFactura.numeroFactura}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    toast.promise(
      html2pdf().set(opt).from(element).save(),
      {
        loading: 'Generando PDF...',
        success: 'PDF descargado correctamente',
        error: 'Error al generar PDF',
      }
    );
  };

  const handlePagarConMP = async (factura) => {
    const facturaAPagar = factura || selectedFactura;
    if (!facturaAPagar) return;
    setMpLoading(true);
    const token = localStorage.getItem('cliente_token');

    try {
      // CORREGIDO: endpoint correcto del backend
      const response = await axios.post(
        `${API_URL}/mercadopago/preferencia/${facturaAPagar.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.init_point) {
        window.open(response.data.init_point, '_blank');
        toast.success('Se abrió Mercado Pago en una nueva pestaña');
      } else {
        toast.error('No se pudo generar el link de pago');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al conectar con Mercado Pago');
    } finally {
      setMpLoading(false);
    }
  };

  const handlePagarConSiro = async (factura) => {
    const facturaAPagar = factura || selectedFactura;
    if (!facturaAPagar) return;
    setSiroLoading(true);
    const token = localStorage.getItem('cliente_token');

    try {
      const response = await axios.post(
        `${API_URL}/siro/boleta/${facturaAPagar.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSiroData(response.data);
      setShowSiroModal(true);
      toast.success('Boleta SIRO generada');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al generar boleta SIRO');
    } finally {
      setSiroLoading(false);
    }
  };

  const getEstadoIcon = (estado) => {
    switch (estado) {
      case 'pagada': return <CheckCircle size={16} className="text-green-600" />;
      case 'vencida': return <AlertTriangle size={16} className="text-red-600" />;
      default: return <Clock size={16} className="text-yellow-600" />;
    }
  };

  const getEstadoClass = (estado) => {
    switch (estado) {
      case 'pagada': return 'bg-green-100 text-green-800';
      case 'vencida': return 'bg-red-100 text-red-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getMetodoIcon = (metodo) => {
    switch (metodo) {
      case 'efectivo': return '💵';
      case 'transferencia': return '🏦';
      case 'mercadopago': return '📱';
      case 'siro': return '🏛️';
      case 'tarjeta': return '💳';
      default: return '💰';
    }
  };

  const totalPendiente = facturas
    .filter(f => f.estado === 'pendiente' || f.estado === 'vencida')
    .reduce((sum, f) => sum + Number(f.total), 0);

  const facturasPendientes = facturas.filter(f => f.estado === 'pendiente' || f.estado === 'vencida');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b print:hidden">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Wifi size={24} className="text-primary-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">PilarNet</h1>
              <p className="text-sm text-gray-500">Portal del Cliente</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600 hidden sm:block">
              {cliente?.nombre} {cliente?.apellido}
            </span>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-1 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <LogOut size={18} />
              <span className="hidden sm:inline">Salir</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Datos del Cliente */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6 print:hidden">
          <div className="flex items-center space-x-3 mb-4">
            <User size={20} className="text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">Mis Datos</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-500">Nombre</p>
              <p className="font-medium">{cliente?.nombre} {cliente?.apellido}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">DNI/CUIT</p>
              <p className="font-medium">{cliente?.dniCuit}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Dirección</p>
              <p className="font-medium">{cliente?.direccion}</p>
            </div>
          </div>
        </div>

        {/* Resumen */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 print:hidden">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Facturas</p>
                <p className="text-2xl font-bold text-gray-900">{facturas.length}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <FileText size={24} className="text-blue-600" />
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm text-gray-500">Saldo Pendiente</p>
                <p className="text-2xl font-bold text-red-600">${totalPendiente.toLocaleString('es-AR')}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <AlertTriangle size={24} className="text-red-600" />
              </div>
            </div>
            {totalPendiente > 0 && (
              <div className="space-y-2">
                <button
                  onClick={() => {
                    if (facturasPendientes.length === 1) {
                      handlePagarConMP(facturasPendientes[0]);
                    } else {
                      const primera = facturasPendientes[0];
                      verDetalleFactura(primera);
                    }
                  }}
                  disabled={mpLoading}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors uppercase font-bold tracking-wide"
                >
                  <CreditCard size={20} />
                  <span>{mpLoading ? 'Conectando...' : 'PAGAR AHORA'}</span>
                </button>
                {siroHabilitado && (
                  <button
                    onClick={() => {
                      if (facturasPendientes.length === 1) {
                        handlePagarConSiro(facturasPendientes[0]);
                      } else {
                        const primera = facturasPendientes[0];
                        verDetalleFactura(primera);
                      }
                    }}
                    disabled={siroLoading}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors uppercase font-bold tracking-wide"
                  >
                    <Barcode size={20} />
                    <span>{siroLoading ? 'Generando...' : 'PAGAR CON SIRO'}</span>
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Facturas Pagadas</p>
                <p className="text-2xl font-bold text-green-600">
                  {facturas.filter(f => f.estado === 'pagada').length}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle size={24} className="text-green-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabla de Facturas */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden print:hidden">
          <div className="p-6 border-b">
            <div className="flex items-center space-x-3">
              <CreditCard size={20} className="text-primary-600" />
              <h2 className="text-lg font-semibold text-gray-900">Mis Facturas</h2>
            </div>
          </div>
          {facturas.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No tenés facturas registradas.
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">N° Factura</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Emisión</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vencimiento</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {facturas.map((factura) => (
                  <tr key={factura.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900">{factura.numeroFactura}</td>
                    <td className="px-6 py-4 text-gray-600">
                      {new Date(factura.fechaEmision).toLocaleDateString('es-AR')}
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {new Date(factura.fechaVencimiento).toLocaleDateString('es-AR')}
                    </td>
                    <td className="px-6 py-4 font-bold text-gray-900">
                      ${Number(factura.total).toLocaleString('es-AR')}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getEstadoClass(factura.estado)}`}>
                        {getEstadoIcon(factura.estado)}
                        <span className="capitalize">{factura.estado}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => verDetalleFactura(factura)}
                          className="flex items-center space-x-1 px-3 py-1 text-sm bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100"
                        >
                          <FileText size={14} />
                          <span>Ver</span>
                        </button>
                        {(factura.estado === 'pendiente' || factura.estado === 'vencida') && (
                          <>
                            <button
                              onClick={() => handlePagarConMP(factura)}
                              disabled={mpLoading}
                              className="flex items-center space-x-1 px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                              title="Pagar con Mercado Pago"
                            >
                              <CreditCard size={14} />
                              <span>Pagar</span>
                            </button>
                            {siroHabilitado && (
                              <button
                                onClick={() => handlePagarConSiro(factura)}
                                disabled={siroLoading}
                                className="flex items-center space-x-1 px-3 py-1 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                                title="Pagar con SIRO"
                              >
                                <Barcode size={14} />
                                <span>SIRO</span>
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Modal Detalle de Factura / Comprobante */}
        {selectedFactura && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 print:hidden">
            <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
              {/* Header del Modal */}
              <div className="p-6 border-b flex items-center justify-between bg-gray-50 rounded-t-xl">
                <h2 className="text-xl font-bold text-gray-900">Detalle de Factura</h2>
                <div className="flex space-x-2">
                  <button
                    onClick={handleDownloadPDF}
                    className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg"
                    title="Descargar PDF"
                  >
                    <Download size={18} />
                  </button>
                  <button
                    onClick={handlePrint}
                    className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg"
                    title="Imprimir"
                  >
                    <Printer size={18} />
                  </button>
                  <button
                    onClick={() => setSelectedFactura(null)}
                    className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>

              {/* Comprobante */}
              <div id="comprobante-factura" className="p-8">
                {/* Encabezado del comprobante */}
                <div className="border-b-2 border-gray-800 pb-4 mb-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <h1 className="text-2xl font-bold text-gray-900">PilarNet</h1>
                      <p className="text-gray-600">Servicios de Internet</p>
                      <p className="text-gray-500 text-sm">Factura {selectedFactura.esFantasia ? 'Fantasía' : 'ARCA'}</p>
                    </div>
                    <div className="text-right">
                      <div className="border-2 border-gray-800 px-4 py-2 inline-block">
                        <p className="text-2xl font-bold">{selectedFactura.tipo}</p>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">N° {selectedFactura.numeroFactura}</p>
                    </div>
                  </div>
                </div>

                {/* Datos del cliente */}
                <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                  <div>
                    <p className="text-gray-500">Cliente</p>
                    <p className="font-medium">{cliente?.nombre} {cliente?.apellido}</p>
                    <p className="text-gray-600">DNI/CUIT: {cliente?.dniCuit}</p>
                    <p className="text-gray-600">{cliente?.direccion}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-500">Fecha de Emisión</p>
                    <p className="font-medium">{new Date(selectedFactura.fechaEmision).toLocaleDateString('es-AR')}</p>
                    <p className="text-gray-500 mt-2">Fecha de Vencimiento</p>
                    <p className="font-medium">{new Date(selectedFactura.fechaVencimiento).toLocaleDateString('es-AR')}</p>
                  </div>
                </div>

                {/* Items */}
                <table className="w-full mb-6">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Concepto</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-600 uppercase">Cant.</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">P. Unit.</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {selectedFactura.items?.map((item) => (
                      <tr key={item.id}>
                        <td className="px-4 py-3">{item.concepto}</td>
                        <td className="px-4 py-3 text-center">{item.cantidad}</td>
                        <td className="px-4 py-3 text-right">${Number(item.precioUnitario).toLocaleString('es-AR')}</td>
                        <td className="px-4 py-3 text-right">${Number(item.subtotal).toLocaleString('es-AR')}</td>
                      </tr>
                    )) || (
                      <tr>
                        <td className="px-4 py-3" colSpan="4">Servicio de Internet</td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {/* Totales */}
                <div className="border-t pt-4">
                  <div className="flex justify-end space-y-1">
                    <div className="w-64">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Subtotal</span>
                        <span className="font-medium">${Number(selectedFactura.subtotal).toLocaleString('es-AR')}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">IVA ({selectedFactura.ivaPorcentaje}%)</span>
                        <span className="font-medium">${Number(selectedFactura.ivaMonto).toLocaleString('es-AR')}</span>
                      </div>
                      <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2">
                        <span>TOTAL</span>
                        <span>${Number(selectedFactura.total).toLocaleString('es-AR')}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Estado + Botones de Pago */}
                <div className="mt-6 pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Estado:</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getEstadoClass(selectedFactura.estado)}`}>
                      {getEstadoIcon(selectedFactura.estado)}
                      <span className="ml-1 capitalize">{selectedFactura.estado}</span>
                    </span>
                  </div>

                  {(selectedFactura.estado === 'pendiente' || selectedFactura.estado === 'vencida') && (
                    <div className="mt-4 space-y-2">
                      <button
                        onClick={() => handlePagarConMP()}
                        disabled={mpLoading}
                        className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <CreditCard size={20} />
                        <span>{mpLoading ? 'Conectando con Mercado Pago...' : 'Pagar con Mercado Pago'}</span>
                      </button>
                      {siroHabilitado && (
                        <button
                          onClick={() => handlePagarConSiro()}
                          disabled={siroLoading}
                          className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <Barcode size={20} />
                          <span>{siroLoading ? 'Generando boleta SIRO...' : 'Pagar con SIRO (Banco Roela)'}</span>
                        </button>
                      )}
                      <p className="text-xs text-gray-400 text-center">
                        Serás redirigido a la pasarela de pago para completar el pago de forma segura.
                      </p>
                    </div>
                  )}
                </div>

                {/* Pagos asociados */}
                {pagosFactura.length > 0 && (
                  <div className="mt-6 pt-4 border-t">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Pagos Registrados</h3>
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Fecha</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Método</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Monto</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Referencia</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {pagosFactura.map((pago) => (
                          <tr key={pago.id}>
                            <td className="px-4 py-2">{new Date(pago.fechaPago).toLocaleDateString('es-AR')}</td>
                            <td className="px-4 py-2">
                              <span className="inline-flex items-center space-x-1">
                                <span>{getMetodoIcon(pago.metodo)}</span>
                                <span className="capitalize">{pago.metodo}</span>
                              </span>
                            </td>
                            <td className="px-4 py-2 text-right font-bold text-green-600">
                              ${Number(pago.monto).toLocaleString('es-AR')}
                            </td>
                            <td className="px-4 py-2 text-gray-500 text-sm">{pago.referencia || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Observaciones */}
                {selectedFactura.observaciones && (
                  <div className="mt-6 pt-4 border-t">
                    <p className="text-sm text-gray-500">Observaciones:</p>
                    <p className="text-sm text-gray-700">{selectedFactura.observaciones}</p>
                  </div>
                )}

                {/* Pie de página */}
                <div className="mt-8 pt-4 border-t text-center text-xs text-gray-400">
                  <p>PilarNet - Servicios de Internet</p>
                  <p>Este documento es un comprobante de factura. Para facturación oficial ante ARCA, contacte a administración.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal SIRO - Opciones de Pago */}
        {showSiroModal && siroData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 print:hidden">
            <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto m-4">
              <div className="p-6 border-b flex items-center justify-between bg-indigo-50 rounded-t-xl">
                <h2 className="text-xl font-bold text-gray-900">Pagar con SIRO</h2>
                <button
                  onClick={() => { setShowSiroModal(false); setSiroData(null); }}
                  className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Info de la factura */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500">Factura</p>
                  <p className="font-bold text-lg">{siroData.numeroFactura}</p>
                  <p className="text-sm text-gray-500 mt-1">Monto a pagar</p>
                  <p className="font-bold text-2xl text-indigo-600">${Number(siroData.monto).toLocaleString('es-AR')}</p>
                </div>

                {/* Opción 1: Billetera Virtual */}
                <div className="border rounded-lg p-4 hover:bg-indigo-50 transition-colors cursor-pointer"
                  onClick={() => window.open(siroData.linkBilletera, '_blank')}>
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <Smartphone size={24} className="text-indigo-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">Billetera Virtual</h3>
                      <p className="text-sm text-gray-500">Pagá desde la app de tu banco</p>
                    </div>
                  </div>
                </div>

                {/* Opción 2: Código de Barras */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <Barcode size={24} className="text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Código de Barras</h3>
                      <p className="text-sm text-gray-500">Pagá en Rapipago, Pago Fácil o banco</p>
                    </div>
                  </div>
                  <div className="bg-gray-100 p-3 rounded text-center font-mono text-sm tracking-widest select-all">
                    {siroData.codigoBarrasFormateado}
                  </div>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(siroData.codigoBarras);
                      toast.success('Código copiado al portapapeles');
                    }}
                    className="mt-2 w-full text-xs text-indigo-600 hover:text-indigo-800"
                  >
                    Copiar código
                  </button>
                </div>

                {/* Opción 3: QR */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <QrCode size={24} className="text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Código QR</h3>
                      <p className="text-sm text-gray-500">Escaneá con cualquier billetera</p>
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <div className="bg-gray-900 text-white p-4 rounded-lg text-center">
                      <QrCode size={120} className="mx-auto mb-2" />
                      <p className="text-xs">QR generado para factura</p>
                      <p className="text-xs font-mono mt-1">{siroData.codigoBarras.slice(-16)}</p>
                    </div>
                  </div>
                </div>

                <p className="text-xs text-gray-400 text-center">
                  Una vez realizado el pago, puede demorar hasta 48 horas en reflejarse en el sistema.
                </p>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
